import { useState } from 'react';
import type { EstadoAplicacion, Momento } from '@copathy/shared';
import { MOMENTO, MOMENTOS } from '@copathy/shared';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Combo } from '../componentes/Combo';
import { Contenedor } from '../componentes/Contenedor';
import { Modal } from '../componentes/Modal';
import { Pestanas, type Pestana } from '../componentes/Pestanas';
import { BuscadorFiltros } from '../componentes/BuscadorFiltros';
import { Enviar } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { usePuede } from '../lib/auth';
import { ErrorApi } from '../lib/api';
import { useAccionesAplicacion, useAplicaciones } from '../lib/aplicaciones';
import { useCuestionarios } from '../lib/cuestionarios';
import { useClientes } from '../lib/clientes';

/**
 * M6 — Aplicaciones: qué cuestionario está aplicado a quién, y hasta cuándo.
 *
 * ── Lo que esta pantalla tiene que responder de un vistazo ──
 * «¿Qué está abierto AHORA?» — porque una aplicación abierta es gente que puede
 * estar respondiendo en este momento, y una que debió cerrarse ayer es un dato
 * contaminándose. Por eso las pestañas son de estado y arranca en «Abiertas».
 */

const PESTANAS_APLICACION = [
  { clave: 'abierta', texto: 'Abiertas' },
  { clave: 'programada', texto: 'Programadas' },
  { clave: 'cerrada', texto: 'Cerradas' },
  { clave: 'todas', texto: 'Todas' },
] as const satisfies readonly Pestana<EstadoAplicacion | 'todas'>[];

const COLOR_ESTADO: Record<EstadoAplicacion, string> = {
  abierta: 'bg-green-100 text-green-900',
  programada: 'bg-marino-50 text-marino-600',
  cerrada: 'bg-marino-100 text-marino-500',
};

const NOMBRE_ESTADO: Record<EstadoAplicacion, string> = {
  abierta: 'Abierta',
  programada: 'Programada',
  cerrada: 'Cerrada',
};

const fecha = (iso: string) =>
  new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function Aplicaciones() {
  const [pestana, setPestana] = useState<EstadoAplicacion | 'todas'>('abierta');
  const [texto, setTexto] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useMigas([{ texto: 'Aplicaciones' }]);

  const consulta = useAplicaciones();
  const { cerrar } = useAccionesAplicacion();
  const puedeAdministrar = usePuede('aplicaciones.administrar');

  /*
    El filtro de estado corre en el cliente: la lista completa ya está en mano
    (no hay paginación todavía) y el estado se deriva del reloj — pedirlo al
    servidor obligaría a revalidar en cada cambio de pestaña sin ganar nada.
  */
  const filtradas = (consulta.data ?? []).filter(
    (a) =>
      (pestana === 'todas' || a.estado === pestana) &&
      (texto.trim() === '' ||
        `${a.cuestionarioNombre} ${a.empresaNombre} ${a.titulo ?? ''}`
          .toLowerCase()
          .includes(texto.trim().toLowerCase())),
  );

  return (
    <Contenedor>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-marino-800">Aplicaciones</h1>
        <p className="mt-1 text-sm text-marino-400">
          Qué cuestionario está aplicado a qué cliente, en qué momento del programa, y hasta
          cuándo se puede responder.
        </p>
      </div>

      <div className="mb-5">
        <BuscadorFiltros
          busqueda={texto}
          alBuscar={setTexto}
          guia="Buscar por cuestionario, cliente o título"
          derecha={
            puedeAdministrar ? (
              <Boton onClick={() => setAplicando(true)} className="py-3">
                Aplicar cuestionario
              </Boton>
            ) : undefined
          }
        />
      </div>

      <Pestanas
        etiqueta="Estado"
        pestanas={PESTANAS_APLICACION}
        activa={pestana}
        alElegir={setPestana}
      />

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}
      {consulta.isError && (
        <p className="py-16 text-center text-red-600">
          No pudimos cargar las aplicaciones. Recarga la página.
        </p>
      )}

      {consulta.data && filtradas.length === 0 && (
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Enviar size={24} />
          </div>
          <h2 className="font-semibold text-marino-700">
            {pestana === 'abierta' ? 'No hay nada abierto ahora' : 'Nada por acá'}
          </h2>
          <p className="mt-1 text-sm text-marino-400">
            Aplicar un cuestionario lo pone en las actividades de la gente de ese cliente.
          </p>
        </div>
      )}

      <ul className="space-y-3">
        {filtradas.map((a) => (
          <li
            key={a.id}
            className={`rounded-2xl border border-marino-100 bg-white p-5 ${
              a.estado === 'cerrada' ? 'opacity-70' : ''
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-semibold text-marino-800">
                    {a.titulo ?? a.cuestionarioNombre}
                  </h2>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${COLOR_ESTADO[a.estado]}`}
                  >
                    {NOMBRE_ESTADO[a.estado]}
                  </span>
                </div>
                <p className="mt-1 text-sm text-marino-400">
                  {a.titulo && <>{a.cuestionarioNombre} · </>}
                  <strong className="font-semibold text-marino-600">{a.empresaNombre}</strong> ·{' '}
                  {MOMENTO[a.momento]} · v{a.version} · {a.preguntas}{' '}
                  {a.preguntas === 1 ? 'pregunta' : 'preguntas'}
                </p>
                <p className="mt-1 text-xs text-marino-400">
                  Abre {fecha(a.abreEn)}
                  {a.cierraEn ? ` · cierra ${fecha(a.cierraEn)}` : ' · sin fecha de cierre'}
                </p>
              </div>

              {puedeAdministrar && a.estado !== 'cerrada' && (
                <Boton
                  variante="secundario"
                  className="px-3.5 py-2 text-sm"
                  cargando={cerrar.isPending}
                  onClick={() => {
                    if (!confirm('Desde este instante nadie más puede responder. ¿Cerrar?')) return;
                    setError(null);
                    cerrar
                      .mutateAsync(a.id)
                      .catch((e) =>
                        setError(e instanceof ErrorApi ? e.message : 'No pudimos cerrarla.'),
                      );
                  }}
                >
                  Cerrar
                </Boton>
              )}
            </div>
          </li>
        ))}
      </ul>

      {aplicando && <ModalAplicar alCerrar={() => setAplicando(false)} />}
    </Contenedor>
  );
}

function ModalAplicar({ alCerrar }: { alCerrar: () => void }) {
  const [cuestionarioId, setCuestionarioId] = useState<number | null>(null);
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [momento, setMomento] = useState<Momento>('antes');
  const [titulo, setTitulo] = useState('');
  const [cierraEn, setCierraEn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});

  const { crear } = useAccionesAplicacion();
  const cuestionarios = useCuestionarios({ activo: 'si' });
  const clientes = useClientes('', { activo: 'si' });

  /*
    Solo se ofrecen cuestionarios CON versión publicada: el servidor lo exige
    igual, pero ofrecer uno sin publicar es invitar al error para responderlo con
    un mensaje.
  */
  const publicados = (cuestionarios.data ?? []).filter((c) => c.versionPublicada !== null);

  async function enviar() {
    setError(null);
    setCampos({});
    if (cuestionarioId === null || empresaId === null) {
      setCampos({
        ...(cuestionarioId === null ? { cuestionarioId: 'Elige el cuestionario.' } : {}),
        ...(empresaId === null ? { empresaId: 'Elige el cliente.' } : {}),
      });
      return;
    }
    try {
      await crear.mutateAsync({
        cuestionarioId,
        empresaId,
        momento,
        titulo: titulo.trim() || null,
        cierraEn: cierraEn ? new Date(cierraEn).toISOString() : null,
      });
      alCerrar();
    } catch (e) {
      if (e instanceof ErrorApi) {
        setError(e.message);
        setCampos(e.campos ?? {});
        return;
      }
      setError('No pudimos aplicar el cuestionario. Intenta de nuevo.');
    }
  }

  return (
    <Modal
      titulo="Aplicar cuestionario"
      ancho="medio"
      abierto
      hayCambios={cuestionarioId !== null || empresaId !== null || titulo.trim().length > 0}
      alCerrar={alCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton onClick={() => void enviar()} cargando={crear.isPending}>
            Aplicar
          </Boton>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-marino-500">
          Queda en las <strong className="font-semibold text-marino-700">actividades</strong> de
          la gente de ese cliente, con la versión publicada de hoy — aunque después se publique
          otra.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <Combo
          etiqueta="Cuestionario"
          opciones={publicados.map((c) => ({
            clave: c.id,
            texto: c.nombre,
            detalle: `v${c.versionPublicada}`,
          }))}
          valor={cuestionarioId}
          alCambiar={(v) => setCuestionarioId(v === null ? null : Number(v))}
          vacio={publicados.length === 0 ? 'No hay cuestionarios publicados' : 'Elige uno'}
          sinCoincidencias="Ninguno coincide."
          error={campos.cuestionarioId}
        />

        <Combo
          etiqueta="Cliente"
          opciones={(clientes.data?.datos ?? []).map((c) => ({ clave: c.id, texto: c.nombre }))}
          valor={empresaId}
          alCambiar={(v) => setEmpresaId(v === null ? null : Number(v))}
          vacio="Elige el cliente"
          sinCoincidencias="Ningún cliente coincide."
          error={campos.empresaId}
        />

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-marino-700">
            Momento del programa
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {MOMENTOS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMomento(m)}
                aria-pressed={momento === m}
                className={`rounded-xl border px-3 py-2 text-sm font-medium transition
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                  ${
                    momento === m
                      ? 'border-naranja-500 bg-naranja-50 text-marino-800'
                      : 'border-marino-100 text-marino-500 hover:border-marino-300'
                  }`}
              >
                {MOMENTO[m]}
              </button>
            ))}
          </div>
        </fieldset>

        <Campo
          etiqueta="Título (opcional) — «Taller CIEC — cierre»"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          error={campos.titulo}
        />

        <Campo
          etiqueta="Cierra el (opcional)"
          type="datetime-local"
          value={cierraEn}
          onChange={(e) => setCierraEn(e.target.value)}
          error={campos.cierraEn}
        />
        <p className="-mt-2 text-xs text-marino-400">
          Sin fecha, queda abierta hasta que alguien la cierre a mano.
        </p>
      </div>
    </Modal>
  );
}
